import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import {
  addWbSlotBlock,
  deleteWbSlotBlock,
  fetchWbSlotActiveSupplies,
  fetchWbSlotAnalytics,
  fetchWbSlotBlocks,
  fetchWbSlotBookings,
  fetchWbSlotConfig,
  fetchWbSlotDemand,
  fetchWbSlotHealth,
  fetchWbSlotOffers,
  patchWbSlotConfig,
  type WbSlotConfig,
  type WbSlotDemand,
  type WbSlotHealth,
  type WbSlotOffer,
} from "../api/WbSlots";

export const useWbSlots = () => {
  const [config, setConfig] = useState<WbSlotConfig | null>(null);
  const [demand, setDemand] = useState<WbSlotDemand[]>([]);
  const [health, setHealth] = useState<WbSlotHealth | null>(null);
  const [offers, setOffers] = useState<WbSlotOffer[]>([]);
  const [offersUpdatedAt, setOffersUpdatedAt] = useState<string | null>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [blocks, setBlocks] = useState<any[]>([]);
  const [supplies, setSupplies] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [demandLoading, setDemandLoading] = useState(false);
  const [blocksLoading, setBlocksLoading] = useState(false);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [suppliesLoading, setSuppliesLoading] = useState(false);
  const saved = useRef<WbSlotConfig | null>(null);

  const loadBlocks = useCallback(async () => {
    setBlocksLoading(true);
    try {
      const rows = await fetchWbSlotBlocks();
      setBlocks(rows);
    } catch {
      message.error("Не удалось загрузить исключения");
    } finally {
      setBlocksLoading(false);
    }
  }, []);

  const loadDemand = useCallback(async () => {
    setDemandLoading(true);
    try {
      const rows = await fetchWbSlotDemand();
      setDemand(rows);
    } catch {
      message.error("Не удалось загрузить окно спроса");
    } finally {
      setDemandLoading(false);
    }
  }, []);

  const loadBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const rows = await fetchWbSlotBookings();
      setBookings(rows);
    } catch {
      message.error("Не удалось загрузить брони");
    } finally {
      setBookingsLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const row = await fetchWbSlotAnalytics();
      setAnalytics(row);
    } catch {
      message.error("Не удалось загрузить аналитику");
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const refreshLive = useCallback(async () => {
    setLiveLoading(true);
    try {
      const h = await fetchWbSlotHealth().catch(() => null);
      setHealth(h);
      const o = await fetchWbSlotOffers().catch(() => ({
        offers: [],
        updatedAt: null,
      }));
      setOffers(o?.offers || []);
      setOffersUpdatedAt(o?.updatedAt || null);
    } catch {
      message.error("Не удалось обновить статус и карту");
    } finally {
      setLiveLoading(false);
    }
  }, []);

  const refreshSupplies = useCallback(async () => {
    setSuppliesLoading(true);
    try {
      const rows = await fetchWbSlotActiveSupplies(true);
      setSupplies(rows);
    } catch {
      message.error("Не удалось загрузить поставки WB");
    } finally {
      setSuppliesLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchWbSlotConfig()
      .then((row) => {
        if (cancelled) return;
        saved.current = row;
        setConfig(row);
      })
      .catch(() => {
        if (!cancelled) message.error("Не удалось загрузить настройки");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const patchLocal = (patch: Partial<WbSlotConfig>) => {
    setConfig((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const save = async (patch: Partial<WbSlotConfig>) => {
    const prev = saved.current;
    if (
      prev &&
      (Object.keys(patch) as (keyof WbSlotConfig)[]).every(
        (key) => prev[key] === patch[key]
      )
    ) {
      return;
    }
    try {
      const next = await patchWbSlotConfig(patch);
      saved.current = next;
      setConfig(next);
      message.success("Сохранено");
    } catch {
      message.error("Не удалось сохранить");
    }
  };

  const excludeWarehouse = (warehouseId: number) =>
    addWbSlotBlock({ warehouse_id: warehouseId }).then(loadBlocks);

  const restoreWarehouse = (id: number) =>
    deleteWbSlotBlock(id).then(loadBlocks);

  return {
    config,
    demand,
    health,
    offers,
    offersUpdatedAt,
    bookings,
    blocks,
    supplies,
    liveLoading,
    demandLoading,
    blocksLoading,
    bookingsLoading,
    analyticsLoading,
    suppliesLoading,
    analytics,
    patchLocal,
    save,
    excludeWarehouse,
    restoreWarehouse,
    refreshLive,
    refreshSupplies,
    loadDemand,
    loadBlocks,
    loadBookings,
    loadAnalytics,
  };
};
